import React, { useEffect, useState } from "react";
import {
  WhmBox,
  WhmFormControl,
  WhmFormHelperText,
  WhmInputLabel,
  WhmMenuItem,
  WhmSelect,
  WhmStack,
  WhmTextField,
  WhmTypography,
} from "@securitize/reactjs-whm";
import { SHARED } from "@securitize/domain";
import { useFormikContext } from "formik";
import { TwoColsInputsContainer } from "../InputContainers";
import { obfuscateString } from "../../../../common/string.utils";
import useTranslations from "../../../../hooks/useTranslations";
import { translationKeys } from "../../../../contexts/I18nContext";
import { InvestorInfo } from "../../../../types/investorInformation.interface";
import MissingInformationCallout from "../MissingInformationCallout";
import { isValidString } from "../../../../helpers/input.utils";
import { getHelperText } from "../../../../helpers/errorHelper";
import { CardInfo } from "../CardInfo";
import { TranslateHtml } from "../../../../components/Translation/Translate";

type Props = {
  disabled?: boolean;
  investorInfo: InvestorInfo;
};

function W8BENTaxInformationInputs({ disabled, investorInfo }: Props) {
  const { getTranslation: t } = useTranslations();
  const formik = useFormikContext<{
    USTaxPayerId: string;
    foreignTaxPayerId: string;
  }>();

  const [disableUSTax, setDisableUSTax] = useState(false);
  const [disableForeignTax, setDisableForeignTax] = useState(false);

  const [usFieldFocused, setUsFieldFocused] = useState(false);
  const [foreignFieldFocused, setForeignFieldFocused] = useState(false);

  useEffect(() => {
    formik.setFieldTouched("foreignTaxPayerId", true, false);
    formik.setFieldTouched("USTaxPayerId", true, false);
  }, []);

  const investorForeignTaxInfo =
    investorInfo?.tax?.filter(
      (taxInfo) => !SHARED.isUsTerritory(taxInfo.taxCountryCode ?? ""),
    ) ?? [];
  const hasMultipleTax = investorForeignTaxInfo.length > 1;
  const errored =
    Boolean(formik.errors.USTaxPayerId) ||
    Boolean(formik.errors.foreignTaxPayerId) ||
    (!isValidString(formik.values.foreignTaxPayerId) &&
      !isValidString(formik.values.USTaxPayerId));

  const handleUSTaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    formik.handleChange(e);
    const value = e.target.value;
    if (value) {
      setDisableForeignTax(true);
      formik.setFieldValue("foreignTaxPayerId", "");
    } else {
      setDisableForeignTax(false);
    }
  };

  const handleForeignTaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    formik.handleChange(e);
    const value = e.target.value;
    if (value) {
      setDisableUSTax(true);
      formik.setFieldValue("USTaxPayerId", "");
    } else {
      setDisableUSTax(false);
    }
  };

  return (
    <WhmStack direction="column" spacing={6} sx={{ paddingTop: "1rem" }}>
      {errored && (
        <MissingInformationCallout
          customText={t(translationKeys.FORM_SECTION_W8BEN_TAX_WARNING)}
        />
      )}
      <TwoColsInputsContainer direction="row">
        <WhmTextField
          disabled={disabled || disableUSTax}
          id="USTaxPayerId"
          name="USTaxPayerId"
          label={t(
            translationKeys.FORM_W8BEN_SECTION_TAX_INFORMATION_US_TAX_ID,
          )}
          value={
            usFieldFocused
              ? formik.values.USTaxPayerId
              : obfuscateString(formik.values.USTaxPayerId)
          }
          onFocus={() => setUsFieldFocused(true)}
          onBlur={(e) => {
            setUsFieldFocused(false);
            formik.handleBlur(e);
          }}
          onChange={handleUSTaxChange}
          error={
            formik.touched.USTaxPayerId && Boolean(formik.errors.USTaxPayerId)
          }
          helperText={
            formik.touched.USTaxPayerId &&
            Boolean(formik.errors.USTaxPayerId) ? (
              <>
                {getHelperText(formik.errors.USTaxPayerId)}
                <WhmBox>
                  <WhmTypography variant="inherit">
                    {t(
                      translationKeys.FORM_SECTION_TAX_INFORMATION_TAX_ID_CALLOUT,
                    )}
                  </WhmTypography>
                </WhmBox>
              </>
            ) : (
              <WhmBox>
                <WhmTypography variant="inherit">
                  {t(
                    translationKeys.FORM_SECTION_TAX_INFORMATION_TAX_ID_CALLOUT,
                  )}
                </WhmTypography>
              </WhmBox>
            )
          }
        />

        {hasMultipleTax ? (
          <WhmFormControl>
            <WhmInputLabel
              id="foreignTaxPayerId"
              error={
                formik.touched.foreignTaxPayerId &&
                Boolean(formik.errors.foreignTaxPayerId)
              }
            >
              {t(
                translationKeys.FORM_W8BEN_SECTION_TAX_INFORMATION_FOREIGN_TAX_ID,
              )}
            </WhmInputLabel>
            <WhmSelect
              id="foreignTaxPayerId"
              name="foreignTaxPayerId"
              labelId="foreignTaxPayerId"
              label={t(
                translationKeys.FORM_W8BEN_SECTION_TAX_INFORMATION_FOREIGN_TAX_ID,
              )}
              value={formik.values.foreignTaxPayerId}
              onBlur={formik.handleBlur}
              onChange={(e) => {
                formik.setFieldValue("foreignTaxPayerId", e.target.value);
                formik.setFieldValue(
                  "taxCountry",
                  investorInfo.tax.find((tax) => tax.taxId === e.target.value)
                    ?.taxCountryCode ?? "",
                );
                if (e.target.value) {
                  setDisableUSTax(true);
                  formik.setFieldValue("USTaxPayerId", "");
                } else {
                  setDisableUSTax(false);
                }
              }}
              disabled={disabled || disableForeignTax}
            >
              {investorForeignTaxInfo.map((taxInfo) => (
                <WhmMenuItem key={taxInfo.taxId} value={taxInfo.taxId}>
                  {taxInfo.taxId}
                </WhmMenuItem>
              ))}
            </WhmSelect>
            <WhmFormHelperText>
              <WhmBox
                sx={{
                  marginTop: "3px !important",
                }}
              >
                <WhmTypography variant="inherit">
                  {t(
                    translationKeys.FORM_SECTION_TAX_INFORMATION_FOREIGN_TAX_ID_CALLOUT,
                  )}
                </WhmTypography>
              </WhmBox>
            </WhmFormHelperText>
          </WhmFormControl>
        ) : (
          <WhmTextField
            disabled={disabled || disableForeignTax}
            id="foreignTaxPayerId"
            name="foreignTaxPayerId"
            label={t(
              translationKeys.FORM_W8BEN_SECTION_TAX_INFORMATION_FOREIGN_TAX_ID,
            )}
            value={
              foreignFieldFocused
                ? formik.values.foreignTaxPayerId
                : obfuscateString(formik.values.foreignTaxPayerId)
            }
            onFocus={() => setForeignFieldFocused(true)}
            onBlur={(e) => {
              setForeignFieldFocused(false);
              formik.handleBlur(e);
            }}
            onChange={handleForeignTaxChange}
            error={
              formik.touched.foreignTaxPayerId &&
              Boolean(formik.errors.foreignTaxPayerId)
            }
            helperText={
              formik.touched.foreignTaxPayerId &&
              Boolean(formik.errors.foreignTaxPayerId) ? (
                <>
                  {getHelperText(formik.errors.foreignTaxPayerId)}
                  <WhmBox>
                    <WhmTypography variant="inherit">
                      {t(
                        translationKeys.FORM_SECTION_TAX_INFORMATION_FOREIGN_TAX_ID_CALLOUT,
                      )}
                    </WhmTypography>
                  </WhmBox>
                </>
              ) : (
                <WhmBox>
                  <WhmTypography variant="inherit">
                    {t(
                      translationKeys.FORM_SECTION_TAX_INFORMATION_FOREIGN_TAX_ID_CALLOUT,
                    )}
                  </WhmTypography>
                </WhmBox>
              )
            }
          />
        )}
      </TwoColsInputsContainer>
      {disabled && (
        <CardInfo
          text={
            <TranslateHtml
              translationKey={translationKeys.FORM_INFO_TAX_FOOTER}
            />
          }
        />
      )}
    </WhmStack>
  );
}

export default W8BENTaxInformationInputs;
